import React, { useCallback, useMemo } from "react";

import deepmerge from "deepmerge";

import PlatformFilterOperator from "@mapmycustomers/shared/enum/PlatformFilterOperator";
import LongLat from "@mapmycustomers/shared/types/base/LongLat";
import {
  EntityType,
  EntityTypeSupportingDataView,
  Group,
} from "@mapmycustomers/shared/types/entity";
import PlatformFilterModel from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";

import DataView from "@app/component/DataView";
import DataViewEntityTypeCount from "@app/component/DataView/type/DataViewEntityTypeCount";
import useMap from "@app/component/map/FeaturedMap/utils/useMap";
import IMapViewContext from "@app/component/view/types/IMapViewContext";
import convertMapFilterToPlatformFilterModel from "@app/util/viewModel/convertMapFilterToPlatformFilterModel";

import withMapView from "../../utils/withMapView";

import styles from "./DataViewWrapper.module.scss";

interface Props extends IMapViewContext {
  entityGroupIds?: Group["id"][];
  entityType?: EntityTypeSupportingDataView;
  entityTypeRecordsCount: DataViewEntityTypeCount;
  hideTabs?: boolean;
  multipolygon?: LongLat[][];
  onHide: () => void;
  totalCount: number;
}

const DataViewWrapper: React.FC<Props> = ({
  entityGroupIds,
  entityType,
  entityTypeRecordsCount,
  hideTabs,
  multipolygon,
  onHide,
  totalCount,
  viewState: { filter, viewAs },
}) => {
  const { map } = useMap();

  const getDrawerContainer = useCallback(() => {
    const selection = map?.getDiv().parentElement?.querySelectorAll(".mmc-map-content");

    if (selection?.length) {
      return selection[0] as HTMLElement;
    }
    return document.body as HTMLElement;
  }, [map]);

  const dataViewFilter: PlatformFilterModel = useMemo(() => {
    let result = convertMapFilterToPlatformFilterModel(
      filter,
      entityType ? [entityType] : [EntityType.COMPANY, EntityType.PERSON, EntityType.DEAL],
      viewAs
    );
    if (entityType && entityGroupIds) {
      result = deepmerge(result, {
        entities: {
          [entityType]: { groups: { [PlatformFilterOperator.GROUP_IN_ANY]: entityGroupIds } },
        },
      });
    }
    if (multipolygon) {
      result = deepmerge(result, { multipolygon });
    }
    return result;
  }, [entityGroupIds, entityType, filter, multipolygon, viewAs]);

  return (
    <DataView
      className={styles.container}
      entityTypeRecordsCount={entityTypeRecordsCount}
      filter={dataViewFilter}
      getDrawerContainer={getDrawerContainer}
      hideTabs={hideTabs}
      onHide={onHide}
      totalCount={totalCount}
      useAnglesCloseButton
    />
  );
};

export default withMapView(DataViewWrapper, ["filter", "viewAs"]);
